// SUNY College of Environmental Science and Forestry: public college page
// (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - ESF's Common Data Set 2025-2026 (CDS_2025_2026_ESF.pdf), linked from
//    Institutional Research's Institutional Data page: C1 applicants, admits
//    and enrollees for Fall 2025 (the totals sum men, women and unknown sex:
//    4,954 applied, 4,197 admitted, 374 enrolled) with the residency table,
//    C8 test policy for Fall 2027, C16 rolling notification, C21/C22 plans.
//    The C1 residency table's own totals are 4,960 applied and 4,191
//    admitted, a few off from the C1 totals; the page shows its rows as
//    given. C21/C22 give no early action counts. G1/G5 are blank.
//  - esf.edu Admissions, First-Year Admission (the plans and dates, the SUNY
//    testing policy, the course requirements). The page carries no year; it
//    is the page as posted for students applying this fall.
//  - Financial Aid, Cost of Attendance Academic Year 2025-26 (Syracuse
//    campus, on-campus housing, Room Plan A, the double room for new
//    students). ESF has not posted a 2026-2027 cost of attendance; the
//    Bursar's 2026-2027 tuition schedule lists full-time undergraduate
//    tuition of $3,535 a semester for New York residents and $10,510 for
//    out-of-state students.
//  - ESF Academic Catalog 2026-27, Degree Programs and Areas of Study (the
//    majors file).
//  - College Scorecard, school id 196103 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'SUNY ESF Common Data Set 2025-2026',
        url: 'https://www.esf.edu/institutional-research/institutional-data.php',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'SUNY ESF Admissions, First-Year Admission',
        url: 'https://www.esf.edu/admissions/apply/first-year/index.php',
        read: '2026-10-01',
    },
    coa: {
        label: 'SUNY ESF Financial Aid, Cost of Attendance Academic Year 2025-26',
        url: 'https://www.esf.edu/tuition-aid/financialaid/costs/2526/index.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?196103-SUNY-College-of-Environmental-Science-and-Forestry',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'suny-college-of-environmental-science-and-forestry',
    kbId: 'suny_college_of_environmental_science_and_forestry',
    name: 'SUNY College of Environmental Science and Forestry',
    shortName: 'SUNY ESF',
    aliases: ['ESF', 'SUNY ESF'],
    location: 'Syracuse, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (ESF communications); decoration only.
    branding: {
        primary: '#046A38',
        secondary: '#FFC72C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/suny_college_of_environmental_science_and_forestry_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 4954,
        admitted: 4197,
        enrolled: 374,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 3798, admitted: 3150 },
            { group: 'Out-of-state', applied: 1030, admitted: 921 },
            { group: 'International', applied: 132, admitted: 120 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (its totals, 4,960 applied and 4,191 admitted, differ slightly from the C1 totals)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'ESF\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by department or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'ESF has no early decision plan. Its Common Data Set gives no early action counts, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Every first-year applicant needs three years each of college preparatory math and science (four are highly recommended). Two programs add to that:',
        rows: [
            { school: 'Landscape Architecture', requirement: 'Art, architectural or technical drawing courses are highly recommended.' },
            { school: '3+3 Doctor of Physical Therapy (with Upstate Medical University)', requirement: 'Apply through the SUNY application, not the Common App.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular admission priority date', value: 'February 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Regular admission (priority date)', deadline: 'February 15', decision: null, binding: false },
        ],
        note: 'Applications after February 15 are considered as space allows, and review starts only once every supporting document arrives. ESF\'s admissions page gives no decision dates; its Common Data Set lists January 15 as the early action notification date and says decisions go out on a rolling basis from September 1. A third path, Guaranteed Transfer (apply by January 15), admits high school seniors to transfer to ESF later, after study at another college.',
        source: 'firstYear',
        binding: 'Early Action is not binding: you get an early answer but do not have to commit until May 1.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SUNY has suspended SAT and ACT requirements for admission to its bachelor\'s degree campuses, ESF included. You choose whether to have scores considered at each SUNY college you apply to.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2025-2026',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [7070, 21030] },
            { item: 'Required fees', amounts: [2200, 2200] },
            { item: 'Housing (on campus, double room)', amounts: [10610, 10610] },
            { item: 'Food (meal plan)', amounts: [8120, 8120] },
            { item: 'Books and supplies', amounts: [1260, 1260] },
            { item: 'Transportation', amounts: [600, 800] },
            { item: 'Personal expenses', amounts: [450, 450] },
        ],
        totals: [30310, 44470],
        note: 'For a new full-time undergraduate on the Syracuse campus, living in a Centennial Hall double room (Room Plan A, for new students). This is the 2025-26 budget, the latest ESF has posted.',
        source: 'coa',
        netPrice: {
            average: 18375,
            byIncome: [
                { income: '$0 to $30,000', amount: 9258 },
                { income: '$30,001 to $48,000', amount: 11486 },
                { income: '$48,001 to $75,000', amount: 17303 },
                { income: '$75,001 to $110,000', amount: 21031 },
                { income: 'Over $110,000', amount: 24772 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from ESF\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
